"use client";

import clsx from "clsx";
import {ReactNode, useCallback, useState} from "react";
import {useTranslation} from "react-i18next";
import Icon, {ICON_ANIMATE} from "@/components/library/Icon";
import {
   Dialog,
   DialogContent,
   DialogDescription,
   DialogFooter,
   DialogHeader,
   DialogTitle,
} from "@/components/ui/dialog";
import {useOrg} from "@/hooks/useOrg";
import {OrgOnboardingDetails} from "@/types/org";
import { apiFetch } from "@/lib/api";

interface ResetOnboardingDialogProps {
   readonly open: boolean;
   readonly onOpenChange: (open: boolean) => void;
}

type ResetStatus = "confirm" | "resetting" | "done" | "error";

interface DeletionSummary {
   scriptsArchived: number;
   useCasesDeleted: number;
   contactsDeleted: number;
   campaignArchived: boolean;
   assistantDeleted: boolean;
   inboundRulesReset: boolean;
   agentProfileReset: boolean;
   notificationsDeleted: number;
   orgReset: boolean;
   errors: string[];
}

interface ItemRow {
   label: string;
   icon: ReactNode;
   present: boolean;
}

function buildItems(
   t: (key: string) => string,
   onboarding?: OrgOnboardingDetails
): ItemRow[] {
   return [
      {
         label: t("resetOnboarding.items.businessHoursScript"),
         icon: <Icon icon="scroll" className="w-4 h-4" />,
         present: Boolean(onboarding?.businessHoursScriptId),
      },
      {
         label: t("resetOnboarding.items.afterHoursScript"),
         icon: <Icon icon="moon" className="w-4 h-4" />,
         present: Boolean(onboarding?.afterHoursScriptId),
      },
      {
         label: t("resetOnboarding.items.outboundScript"),
         icon: <Icon icon="phone" className="w-4 h-4" />,
         present: Boolean(onboarding?.outboundScriptId),
      },
      {
         label: t("resetOnboarding.items.outboundCampaign"),
         icon: <Icon icon="file-lines" className="w-4 h-4" />,
         present: Boolean(onboarding?.outboundCampaignId),
      },
      {
         label: t("resetOnboarding.items.gailgptAssistant"),
         icon: <Icon icon="message" className="w-4 h-4" />,
         present: Boolean(onboarding?.gailGptAssistantId),
      },
      {
         label: t("resetOnboarding.items.agentProfile"),
         icon: <Icon icon="gear" className="w-4 h-4" />,
         present: true, // always attempt reset
      },
      {
         label: t("resetOnboarding.items.notifications"),
         icon: <Icon icon="bell" className="w-4 h-4" />,
         present: true, // always attempt
      },
      {
         label: t("resetOnboarding.items.allContacts"),
         icon: <Icon icon="book-user" className="w-4 h-4" />,
         present: true, // always attempt
      },
      {
         label: t("resetOnboarding.items.allUseCasesScripts"),
         icon: <Icon icon="trash" className="w-4 h-4" />,
         present: true,
      },
      {
         label: t("resetOnboarding.items.businessInfoProgress"),
         icon: <Icon icon="arrow-rotate-left" className="w-4 h-4" />,
         present: true,
      },
   ];
}

export default function ResetOnboardingDialog({
   open,
   onOpenChange,
}: ResetOnboardingDialogProps) {
   const {org} = useOrg();
   const {t} = useTranslation("stats");
   const {t: ct} = useTranslation("common");
   const onboarding = org?.details?.onboarding;

   const [status, setStatus] = useState<ResetStatus>("confirm");
   const [summary, setSummary] = useState<DeletionSummary | null>(null);

   const items = buildItems(t, onboarding);

   const handleReset = useCallback(async () => {
      setStatus("resetting");

      try {
         const response = await apiFetch<{success: boolean; summary: DeletionSummary}>(
           "/api/onboarding/reset",
           "POST",
           {
           body: {
            businessHoursScriptId: onboarding?.businessHoursScriptId,
            afterHoursScriptId: onboarding?.afterHoursScriptId,
            outboundScriptId: onboarding?.outboundScriptId,
            outboundCampaignId: onboarding?.outboundCampaignId,
            gailGptAssistantId: onboarding?.gailGptAssistantId,
         },
         },
         );

         if (response) {
            setSummary(response.summary);
            setStatus("done");
         } else {
            setStatus("error");
         }
      } catch (e) {
         console.error("Reset failed:", e);
         setStatus("error");
      }
   }, [onboarding]);

   const handleClose = useCallback(() => {
      if (status === "done") {
         // Reload the page so the fresh org state is picked up everywhere
         window.location.reload();
         return;
      }
      setStatus("confirm");
      setSummary(null);
      onOpenChange(false);
   }, [status, onOpenChange]);

   return (
      <Dialog open={open} onOpenChange={handleClose} modal>
         <DialogContent
            className="max-w-md"
            onPointerDownOutside={(e) => {
               if (status === "resetting") e.preventDefault();
            }}
         >
            {/* ── Confirm view ─────────────────────────────────────── */}
            {status === "confirm" && (
               <>
                  <DialogHeader>
                     <DialogTitle className="flex items-center gap-2 text-destructive">
                        <Icon icon="triangle-exclamation" className="w-5 h-5" />
                        {t("resetOnboarding.title")}
                     </DialogTitle>
                     <DialogDescription>
                        {t("resetOnboarding.description")}
                     </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-1.5 py-2">
                     <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        {t("resetOnboarding.deletionHeader")}
                     </p>
                     <ul className="space-y-1">
                        {items.map((item) => (
                           <li
                              key={item.label}
                              className={clsx(
                                 "flex items-center gap-2 text-sm rounded-md px-2 py-1.5",
                                 item.present
                                    ? "text-foreground bg-destructive/5"
                                    : "text-muted-foreground/50 line-through"
                              )}
                           >
                              {item.icon}
                              {item.label}
                           </li>
                        ))}
                     </ul>
                  </div>

                  <DialogFooter>
                     <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-medium rounded-lg border border-200 text-foreground hover:bg-100 transition-colors"
                     >
                        {ct("cancel")}
                     </button>
                     <button
                        type="button"
                        onClick={handleReset}
                        className="px-4 py-2 text-sm font-medium rounded-lg bg-destructive text-white hover:bg-destructive/90 transition-colors"
                     >
                        {t("resetOnboarding.resetEverything")}
                     </button>
                  </DialogFooter>
               </>
            )}

            {/* ── Resetting view ────────────────────────────────────── */}
            {status === "resetting" && (
               <div className="flex flex-col items-center gap-4 py-8">
                  <Icon icon="spinner" animate={ICON_ANIMATE.SPIN} className="w-8 h-8 text-primary" />
                  <div className="text-center space-y-1">
                     <p className="text-base font-semibold text-foreground">
                        {t("resetOnboarding.resetting")}
                     </p>
                     <p className="text-sm text-muted-foreground">
                        {t("resetOnboarding.resettingDescription")}
                     </p>
                  </div>
               </div>
            )}

            {/* ── Done view ─────────────────────────────────────────── */}
            {status === "done" && summary && (
               <>
                  <DialogHeader>
                     <DialogTitle className="flex items-center gap-2 text-emerald-600">
                        <Icon icon="check" className="w-5 h-5" />
                        {t("resetOnboarding.doneTitle")}
                     </DialogTitle>
                     <DialogDescription>
                        {t("resetOnboarding.doneDescription")}
                     </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-1.5 py-2 text-sm">
                     <SummaryRow
                        label={t("resetOnboarding.summaryLabels.scriptsArchived")}
                        value={summary.scriptsArchived}
                     />
                     <SummaryRow
                        label={t("resetOnboarding.summaryLabels.useCasesDeleted")}
                        value={summary.useCasesDeleted}
                     />
                     <SummaryRow
                        label={t("resetOnboarding.summaryLabels.contactsDeleted")}
                        value={summary.contactsDeleted}
                     />
                     <SummaryRow
                        label={t("resetOnboarding.summaryLabels.campaignArchived")}
                        value={
                           summary.campaignArchived
                              ? t("resetOnboarding.yes")
                              : t("resetOnboarding.na")
                        }
                     />
                     <SummaryRow
                        label={t("resetOnboarding.summaryLabels.assistantDeleted")}
                        value={
                           summary.assistantDeleted
                              ? t("resetOnboarding.yes")
                              : t("resetOnboarding.na")
                        }
                     />
                     <SummaryRow
                        label={t("resetOnboarding.summaryLabels.inboundRulesReset")}
                        value={
                           summary.inboundRulesReset
                              ? t("resetOnboarding.yes")
                              : t("resetOnboarding.no")
                        }
                     />
                     <SummaryRow
                        label={t("resetOnboarding.summaryLabels.agentProfileReset")}
                        value={
                           summary.agentProfileReset
                              ? t("resetOnboarding.yes")
                              : t("resetOnboarding.no")
                        }
                     />
                     <SummaryRow
                        label={t("resetOnboarding.summaryLabels.notificationsDeleted")}
                        value={summary.notificationsDeleted}
                     />
                     <SummaryRow
                        label={t("resetOnboarding.summaryLabels.orgDetailsReset")}
                        value={
                           summary.orgReset ? t("resetOnboarding.yes") : t("resetOnboarding.no")
                        }
                     />

                     {summary.errors.length > 0 && (
                        <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-1">
                           <p className="text-xs font-medium text-amber-600">
                              {t("resetOnboarding.nonFatalErrors", {
                                 count: summary.errors.length,
                              })}
                           </p>
                           {summary.errors.map((err, i) => (
                              <p key={i} className="text-xs text-amber-600/80 truncate">
                                 {err}
                              </p>
                           ))}
                        </div>
                     )}
                  </div>

                  <DialogFooter>
                     <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
                     >
                        {t("resetOnboarding.closeAndReload")}
                     </button>
                  </DialogFooter>
               </>
            )}

            {/* ── Error view ────────────────────────────────────────── */}
            {status === "error" && (
               <>
                  <DialogHeader>
                     <DialogTitle className="flex items-center gap-2 text-destructive">
                        <Icon icon="xmark" className="w-5 h-5" />
                        {t("resetOnboarding.errorTitle")}
                     </DialogTitle>
                     <DialogDescription>
                        {t("resetOnboarding.errorDescription")}
                     </DialogDescription>
                  </DialogHeader>

                  <DialogFooter>
                     <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-medium rounded-lg border border-200 text-foreground hover:bg-100 transition-colors"
                     >
                        {ct("cancel")}
                     </button>
                     <button
                        type="button"
                        onClick={handleReset}
                        className="px-4 py-2 text-sm font-medium rounded-lg bg-destructive text-white hover:bg-destructive/90 transition-colors"
                     >
                        {ct("retry")}
                     </button>
                  </DialogFooter>
               </>
            )}
         </DialogContent>
      </Dialog>
   );
}

function SummaryRow({label, value}: {label: string; value: string | number}) {
   return (
      <div className="flex justify-between items-center px-2 py-1 rounded-md bg-100/50">
         <span className="text-muted-foreground">{label}</span>
         <span className="font-medium text-foreground">{String(value)}</span>
      </div>
   );
}
